import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Fab from '@mui/material/Fab';
import Button from '@mui/material/Button';
import Zoom from '@mui/material/Zoom';
import Tooltip from '@mui/material/Tooltip';
import Slide from '@mui/material/Slide';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';
import { TransitionProps } from '@mui/material/transitions';

import { selectors as selectorsLogin } from '../../containers/LoginPage/reducer';
import { selectors as ordersSelectors } from '../../containers/OrdersPage/reducer';
import { addApplicationNotification } from '../../containers/InitComponent/actions';
import { SupportService } from './service';
import { PROBLEM_TYPES } from './constants';
import {
  buildFormData,
  formatProblemTime,
  getServerErrorMessage,
  isValidDateTime,
  maskDateTime,
  normalizeErrorDetail,
} from './helpers';
import {
  SupportFormValues,
  SupportUserData,
  SupportWidgetProps,
} from './types';
import useIsMobile from '../../global/hooks/useIsMobile';

const BRAND_COLOR = '#1d618f';
const BRAND_COLOR_HOVER = '#1d498f';

const Transition = React.forwardRef(
  (
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
);
Transition.displayName = 'SupportWidgetTransition';

const SupportIcon: React.FC = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 2a8 8 0 0 0-8 8v5a3 3 0 0 0 3 3h1v-7H6v-1a6 6 0 1 1 12 0v1h-2v7h1a3 3 0 0 0 3-3v-5a8 8 0 0 0-8-8Z"
      fill="currentColor"
    />
    <path
      d="M9 19a3 3 0 0 0 3 3h2a1 1 0 1 0 0-2h-2a1 1 0 0 1-1-1H9Z"
      fill="currentColor"
    />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="m6 6 12 12M18 6 6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
    <path
      d="M12 7v5l3 2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SupportWidget: React.FC<SupportWidgetProps> = ({ defaultOpen }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const [open, setOpen] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([]);

  // Автооткрытие по ссылке `?tickets=true` — ровно один раз, чтобы закрытая
  // пользователем модалка не всплывала снова на ре-рендерах родителя.
  const wasAutoOpened = useRef(false);

  useEffect(() => {
    if (defaultOpen && !wasAutoOpened.current) {
      wasAutoOpened.current = true;
      setOpen(true);
    }
  }, [defaultOpen]);

  const userData = useSelector(
    selectorsLogin.userData
  ) as SupportUserData | null;
  const orderMatch = useRouteMatch<{ orderId: string }>([
    '/orders/:orderId',
    '/test-orders/:orderId',
  ]);
  const selectedOrder = useSelector(ordersSelectors.selectedOrderData) as {
    orderNumber?: string;
  } | null;
  const orderNumber = orderMatch ? selectedOrder?.orderNumber ?? '' : '';

  const pharmacy = userData?.networkName ?? '';
  const sourceCode = userData?.sourceCode ?? '';

  const initialValues: SupportFormValues = {
    full_name: userData?.name ?? '',
    phone: userData?.phone ?? '',
    pharmacy_name: pharmacy,
    pharmacy_source_code: sourceCode,
    error_time: formatProblemTime(new Date()),
    order_number: orderNumber,
    problem_type: '',
    description: '',
    actions_done: '',
    extra_info: '',
    anydesk_id: '',
  };

  const validationSchema = Yup.object({
    full_name: Yup.string().trim(),
    // имя больше не обяхательно
    // .required(t('SupportWidget.VALIDATION.REQUIRED')),
    phone: Yup.string().trim().required(t('SupportWidget.VALIDATION.REQUIRED')),
    pharmacy_name: Yup.string()
      .trim()
      .required(t('SupportWidget.VALIDATION.REQUIRED')),
    problem_type: Yup.string().required(t('SupportWidget.VALIDATION.REQUIRED')),
    description: Yup.string()
      .trim()
      .required(t('SupportWidget.VALIDATION.REQUIRED')),
    error_time: Yup.string()
      .trim()
      .required(t('SupportWidget.VALIDATION.REQUIRED'))
      .test('datetime', t('SupportWidget.VALIDATION.DATE'), (value) =>
        isValidDateTime((value ?? '').trim())
      ),
  });

  const handleClose = (): void => {
    setOpen(false);
    setFiles([]);
  };

  const handleFilesChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files) {
      const selected = Array.from(event.target.files);
      setFiles((prev) => [...prev, ...selected]);
      event.target.value = '';
    }
  };

  const handleRemoveFile = (index: number): void => {
    setFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
  };

  const handleSubmit = async (
    values: SupportFormValues,
    { resetForm }: FormikHelpers<SupportFormValues>
  ): Promise<void> => {
    try {
      const result = await SupportService.createTicket(
        buildFormData(values, files)
      );

      if (result.ok) {
        dispatch(
          addApplicationNotification({
            type: 'success',
            content: t('SupportWidget.SUCCESS', {
              number: result.ticketNumber ?? '',
            }),
            canBeClosed: true,
          })
        );
        resetForm();
        setFiles([]);
        setOpen(false);
      } else {
        dispatch(
          addApplicationNotification({
            type: 'error',
            content: normalizeErrorDetail(result.detail),
            canBeClosed: true,
          })
        );
      }
    } catch (error) {
      dispatch(
        addApplicationNotification({
          type: 'error',
          content: getServerErrorMessage(error),
          canBeClosed: true,
        })
      );
    }
  };

  return (
    <>
      <Zoom in>
        <Tooltip
          title={t('SupportWidget.TOOLTIP') as string}
          placement="left"
          arrow
        >
          <Fab
            aria-label={t('SupportWidget.BUTTON')}
            onClick={() => setOpen(true)}
            sx={{
              position: 'fixed',
              top: 270,
              right: 70,
              zIndex: 1200,
              color: '#fff',
              backgroundColor: BRAND_COLOR,
              '&:hover': { backgroundColor: BRAND_COLOR_HOVER },
            }}
          >
            <SupportIcon />
          </Fab>
        </Tooltip>
      </Zoom>

      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            ...(isMobile && { px: 2, py: 1.5, fontSize: '1.05rem' }),
          }}
        >
          <span>{t('SupportWidget.MODAL_TITLE')}</span>
          <IconButton
            onClick={handleClose}
            aria-label={t('SupportWidget.CLOSE')}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Formik<SupportFormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            handleChange,
            handleBlur,
            setFieldValue,
            handleSubmit: submitForm,
          }: FormikProps<SupportFormValues>) => (
            <form onSubmit={submitForm} noValidate>
              <DialogContent dividers sx={isMobile ? { px: 2 } : undefined}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {t('SupportWidget.SUBTITLE')}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    mb: 2,
                    p: 1.5,
                    borderRadius: '12px',
                    backgroundColor: '#eef2ff',
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      border: '1px solid #dbe4ff',
                      backgroundColor: '#fff',
                      color: '#4c6ef5',
                    }}
                  >
                    <ClockIcon />
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: '#1f2937', mb: 0.25 }}
                    >
                      {t('SupportWidget.WORKING_HOURS.TITLE')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: '13px', color: '#6b7280' }}
                    >
                      {t('SupportWidget.WORKING_HOURS.DESCRIPTION')}
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={isMobile ? 1.5 : 2}>
                  <TextField
                    fullWidth
                    size="small"
                    name="full_name"
                    label={t('SupportWidget.FIELDS.NAME')}
                    value={values.full_name}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    name="phone"
                    label={t('SupportWidget.FIELDS.PHONE')}
                    value={values.phone}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    name="pharmacy_name"
                    label={t('SupportWidget.FIELDS.PHARMACY_NAME')}
                    value={values.pharmacy_name}
                    InputProps={{ readOnly: true }}
                  />
                  {!!orderNumber && (
                    <TextField
                      fullWidth
                      size="small"
                      name="order_number"
                      label={t('SupportWidget.FIELDS.ORDER_NUMBER')}
                      value={values.order_number}
                      InputProps={{ readOnly: true }}
                    />
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    name="error_time"
                    label={t('SupportWidget.FIELDS.PROBLEM_TIME')}
                    placeholder="ДД.ММ.ГГГГ ЧЧ:ММ"
                    value={values.error_time}
                    onChange={(event) =>
                      setFieldValue(
                        'error_time',
                        maskDateTime(event.target.value)
                      )
                    }
                    onBlur={handleBlur}
                    error={touched.error_time && Boolean(errors.error_time)}
                    helperText={touched.error_time && errors.error_time}
                    inputProps={{ inputMode: 'numeric', maxLength: 16 }}
                  />
                  <TextField
                    select
                    fullWidth
                    size="small"
                    name="problem_type"
                    label={t('SupportWidget.FIELDS.PROBLEM_TYPE')}
                    value={values.problem_type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.problem_type && Boolean(errors.problem_type)}
                    helperText={touched.problem_type && errors.problem_type}
                  >
                    {PROBLEM_TYPES.map((type) => (
                      <MenuItem key={type.label} value={type.label}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {(() => {
                    const selectedProblem = PROBLEM_TYPES.find(
                      (type) => type.label === values.problem_type
                    );
                    if (!selectedProblem?.description) return null;
                    return (
                      <Alert severity="info" sx={{ mt: -1 }}>
                        <AlertTitle sx={{ mb: 0.25 }}>
                          {selectedProblem.label}
                        </AlertTitle>
                        {selectedProblem.description}
                      </Alert>
                    );
                  })()}
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={3}
                    name="description"
                    label={t('SupportWidget.FIELDS.DESCRIPTION')}
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    name="actions_done"
                    label={t('SupportWidget.FIELDS.ACTIONS_DONE')}
                    value={values.actions_done}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    name="extra_info"
                    label={t('SupportWidget.FIELDS.EXTRA_INFO')}
                    value={values.extra_info}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    name="anydesk_id"
                    label={t('SupportWidget.FIELDS.ANYDESK_ID')}
                    value={values.anydesk_id}
                    onChange={(event) =>
                      setFieldValue(
                        'anydesk_id',
                        event.target.value.replace(/\D/g, '')
                      )
                    }
                    onBlur={handleBlur}
                    inputProps={{ inputMode: 'numeric', maxLength: 12 }}
                  />

                  <Box>
                    <Button
                      component="label"
                      variant="outlined"
                      sx={{
                        textTransform: 'none',
                        color: BRAND_COLOR,
                        borderColor: BRAND_COLOR,
                        '&:hover': { borderColor: BRAND_COLOR_HOVER },
                      }}
                    >
                      {t('SupportWidget.FIELDS.ATTACH_FILES')}
                      <input
                        type="file"
                        hidden
                        multiple
                        accept="image/*,video/*,.log,.txt,.pdf,.doc,.docx,.xls,.xlsx"
                        onChange={handleFilesChange}
                      />
                    </Button>
                    {files.length > 0 && (
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={1}
                        sx={{ mt: 1.5 }}
                      >
                        {files.map((file, index) => (
                          <Chip
                            key={`${file.name}-${file.size}-${file.lastModified}`}
                            label={file.name}
                            onDelete={() => handleRemoveFile(index)}
                            size="small"
                            sx={{ maxWidth: '100%' }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </DialogContent>

              <DialogActions
                sx={{
                  px: 3,
                  py: 2,
                  ...(isMobile && {
                    px: 2,
                    py: 1.5,
                    flexDirection: 'column-reverse',
                    alignItems: 'stretch',
                    gap: 1,
                    '& > :not(:first-of-type)': { ml: 0 },
                  }),
                }}
              >
                <Button
                  onClick={handleClose}
                  fullWidth={isMobile}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  {t('SupportWidget.CANCEL')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  fullWidth={isMobile}
                  sx={{
                    textTransform: 'none',
                    minWidth: 160,
                    backgroundColor: BRAND_COLOR,
                    '&:hover': { backgroundColor: BRAND_COLOR_HOVER },
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    t('SupportWidget.SUBMIT')
                  )}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default SupportWidget;
